export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let lastFunc: ReturnType<typeof setTimeout> | null = null;
  let lastRan: number | null = null;

  return function (...args: Parameters<T>) {
    const now = Date.now();

    if (lastRan && now - lastRan < limit) {
      if (lastFunc) {
        clearTimeout(lastFunc);
      }
      lastFunc = setTimeout(
        () => {
          lastRan = Date.now();
          func(...args);
        },
        limit - (now - lastRan),
      );
    } else {
      lastRan = now;
      func(...args);
    }
  };
}

export function getRandomID(length: number): string {
  const timestamp = Date.now().toString();
  const timestampSplitIndex = Math.floor(timestamp.length / 2);
  const timestampPart1 = timestamp.slice(0, timestampSplitIndex);
  const timestampPart2 = timestamp.slice(timestampSplitIndex);

  const adjustedLength = length - timestamp.length;
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;

  for (let i = 0; i < adjustedLength; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  const splitIndex1 = Math.floor(result.length / 3);
  const splitIndex2 = Math.floor((2 * result.length) / 3);

  const part1 = result.slice(0, splitIndex1);
  const part2 = result.slice(splitIndex1, splitIndex2);
  const part3 = result.slice(splitIndex2);

  return `${part1}${timestampPart1}${part2}${timestampPart2}${part3}`;
}

export function getFormattedDate(date: Date = new Date()): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
}

export function escapeXMLSitemap(str: string = ''): string {
  // Remove or replace characters not allowed in XML 1.0
  // Allowed: #x9 | #xA | #xD | [#x20-#xD7FF] | [#xE000-#xFFFD] | [#x10000-#x10FFFF]
  // Remove invalid control characters and encode special XML entities
  if (!str || typeof str !== 'string') return '';

  return String(str)
    .replace(
      /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u0084\u0086-\u009F]/g,
      '',
    )
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function numberToString(
  number: number = 0,
  iteration: number = 0,
): string {
  const suffixes = ['', 'k', 'm', 'b', 't'];
  if (number < 1000) return number + suffixes[iteration];
  const newNumber = Math.floor(number / 100) / 10;
  return numberToString(newNumber, iteration + 1);
}
